import { createFeatureLayer } from '@rsos/maps/providers/esri/layers/createFeatureLayer';
import createMapImageLayer from '@rsos/maps/providers/esri/layers/createMapImageLayer';
import checkLayerVisiblity from './checkLayerVisiblity';
import { getGISLayerID } from './getGISLayerID';
import type {
  CreateLayerParams,
  Layer,
  ParentLayer,
  GISService,
  LayerData,
  LayerPromise,
  SourceJSON,
} from './getLayerType.types';

type LayerType = 'feature' | 'map-image';

/**
 * Helper function to generate unique layer names by appending numbers for duplicates
 */
const getUniqueLayerName = (
  baseTitle: string,
  usedTitles: Map<string, number>,
  sourceJSON: SourceJSON,
): string => {
  if (sourceJSON.name) {
    return sourceJSON.name;
  }
  if (!usedTitles.has(baseTitle)) {
    usedTitles.set(baseTitle, 0);
    return baseTitle;
  }
  const count = usedTitles.get(baseTitle)! + 1;
  usedTitles.set(baseTitle, count);
  return `${baseTitle}_${count}`;
};

/**
 * Recursively extracts and creates layers from nested sublayers.
 *
 * This utility function handles GIS layers with complex nested structures.
 * Unlike most other GIS layers that have sublayers only 1 level deep, some services
 * have complex nested hierarchies that go multiple levels deep.
 *
 * The function handles the complex nesting by:
 * - Recursively traversing sublayers until it reaches the deepest level (no more sublayers)
 * - Building hierarchical ID paths to maintain parent-child relationships
 * - Creating individual layers for each deepest sublayer
 * - Generating unique titles to avoid duplicates
 * - Preserving the original service configuration while adding sublayer-specific metadata
 *
 * @param rootLayer - The root layer object containing nested sublayers
 * @param service - The GIS service configuration object
 * @param layerData - Layer configuration data including apiKey, visible, opacity settings
 * @param layerType - Type of layer to create ('feature' or 'map-image')
 * @returns Array of promises that resolve to layer objects with associated service metadata
 */
export const getNestedLayers = (
  rootLayer: ParentLayer | null | undefined,
  service: GISService,
  layerData: LayerData,
  layerType: LayerType,
): LayerPromise<Layer>[] => {
  const deepestLayerPromises: LayerPromise<Layer>[] = [];
  const usedTitles = new Map<string, number>();

  const recursivelyGetDeepestLayers = (
    layer: ParentLayer,
    parentIDPath = '',
  ): void => {
    const sublayers = layer.sublayers || layer.layers || null;

    if (!sublayers || sublayers.items.length === 0) {
      /**
       * `layer.layerId` can be 0 (a valid first index) and is therefore falsy.
       * Use explicit undefined/null checks instead of a truthy test.
       */
      const id =
        layer.layerId !== undefined && layer.layerId !== null
          ? layer.layerId
          : layer.id;

      // NOTE: Due to current MapLayersControl limitations, only single-level nesting of layers is supported.
      // Some map services (e.g., traffic) have very deep hierarchies (4+ levels) before reaching a data layer:
      // Example:
      // Root
      //  └─ Group A
      //      └─ Group B
      //          └─ Group C
      //              └─ Data Layer (actual content)
      //          └─ Group D
      //              └─ Data Layer (actual content)
      //
      // Since sublayers within a service can share the same name (especially with different parents),
      // getUniqueLayerName ensures each layer's name is unique by appending a count if necessary.
      const baseTitle = layer.title || layer.name || 'Untitled Layer';
      const sourceJSON = layer.sourceJSON || {};
      const uniqueTitle = getUniqueLayerName(baseTitle, usedTitles, sourceJSON);
      if (service.attributes?.layer_settings?.length) {
        const thisSublayerSetting = service.attributes?.layer_settings?.find(
          setting => setting.layer_id === String(id),
        );
        const shouldSublayerBeDisplayed = thisSublayerSetting?.visible;
        if (!shouldSublayerBeDisplayed) {
          return;
        }
      }

      const layerParams: CreateLayerParams = {
        apiKey: layerData.apiKey,
        id: `${getGISLayerID(service)}-sublayer-${id}`,
        title: uniqueTitle,
        visible: layerData.visible,
        opacity: layerData.opacity,
        // Layer-type specific properties
        ...(layerType === 'feature'
          ? {
              url: `${layer.url}/${id}`,
              outFields: layerData.outFields || ['*'],
            }
          : {
              url: service.gis_service_url,
              timeInfo: null,
              sublayers: [{ id: id!, visible: true }],
            }),
      };

      const createLayer =
        layerType === 'feature' ? createFeatureLayer : createMapImageLayer;

      const layerPromise: LayerPromise<Layer> = (
        createLayer as unknown as (params: CreateLayerParams) => Promise<Layer>
      )(layerParams)
        .then((subLayer: Layer) => {
          if (subLayer?.loaded) {
            const loadedSubLayer = checkLayerVisiblity(subLayer);

            return {
              layer: loadedSubLayer,
              service: {
                ...service,
                layerIndex: id!,
                gis_service_url: subLayer.parsedUrl!.path,
                sublayerID: `${getGISLayerID(service)}-sublayer-${id}`,
                sublayerName: uniqueTitle,
                wkid: subLayer?.spatialReference?.wkid,
                parentIDPath: parentIDPath,
              },
            };
          }
          return null;
        })
        .catch(() => {
          // fail silently
          return null;
        });

      deepestLayerPromises.unshift(layerPromise);
      return;
    }

    // If sublayers exist, recursively process each one
    // Parent layers use simple IDs, complex ID logic only applies to final sublayers
    const currentID = layer.layerId || layer.id;
    const newParentIDPath = parentIDPath
      ? `${parentIDPath}-${currentID}`
      : String(currentID);
    for (const sublayer of sublayers.items) {
      recursivelyGetDeepestLayers(sublayer, newParentIDPath);
    }
  };

  // Handle the case where the input layer itself might be null/undefined
  if (!rootLayer) {
    return deepestLayerPromises;
  }

  // Start the recursion with empty parentIDPath to avoid duplication
  recursivelyGetDeepestLayers(rootLayer, '');

  return deepestLayerPromises;
};

// Convenience functions for backward compatibility
export const getNestedFeatureLayer = (
  rootLayer: ParentLayer | null | undefined,
  service: GISService,
  layerData: LayerData,
) => getNestedLayers(rootLayer, service, layerData, 'feature');

export const getNestedMapImageLayer = (
  rootLayer: ParentLayer | null | undefined,
  service: GISService,
  layerData: LayerData,
) => getNestedLayers(rootLayer, service, layerData, 'map-image');
